<template>
  <v-app :dark="theme.isDark">
    <v-layout
      v-if="!currentUserLoaded || !teamResolved"
      row
      justify-center
      align-center
    >
      <spinner />
    </v-layout>

    <template v-else-if="isUserLoggedIn">
      <div class="app-layout">
        <app-sidebar v-if="!inIframe && !hideSidebar" />
        <router-view />
      </div>

      <chat-agent @sidebar-change="handleDockedSidebarChange" />
      <portal-target name="docked-panel" multiple slim />
      <app-modals />
    </template>
  </v-app>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import theme from '@/factories/theme';
import { inIframe } from '@/globals';
import intercom from '@/lib/intercom';
import { initAppAnalytics } from '@/lib/analytics';
import api from '@/factories/api';
import { update as updateIntercom } from '@intercom/messenger-js-sdk';
import { FEATURES } from '@/access/features';
import { clearTeamInTab, showTeamInTab } from '@/lib/teamTab';
import EventBus from '@/factories/eventBus';

import AppSidebar from '@/components/layouts/sidebar/AppSidebar';
import AppModals from '@/components/layouts/AppModals';
import ChatAgent from '@/components/basic/chat-agent/ChatAgent.vue';

export default {
  name: 'AppLayout',
  components: {
    AppSidebar,
    AppModals,
    ChatAgent,
  },
  data() {
    return {
      theme,
      inIframe,
      jwtRefreshing: false,
      teamResolved: false,
      dockedSidebarCount: 0,
    };
  },
  computed: {
    ...mapState('users', ['currentUser', 'currentUserLoaded']),
    ...mapGetters('users', ['isUserLoggedIn']),
    ...mapGetters('teams', ['currentTeam']),
    hideSidebar() {
      return this.$route.meta?.hideSidebar;
    },
    // null outside team context: teams off, unresolved, or admin "all teams"
    tabTeam() {
      if (!this.teamResolved || !this.isFeatureEnabled(FEATURES.TEAMS)) {
        return null;
      }
      return this.currentTeam || null;
    },
    tabTeamSignature() {
      const { id, label, icon, color } = this.tabTeam || {};
      return this.tabTeam ? [id, label, icon, color].join('|') : '';
    },
  },
  watch: {
    tabTeamSignature: {
      immediate: true,
      handler() {
        if (this.tabTeam) {
          showTeamInTab(this.tabTeam);
        } else {
          clearTeamInTab();
        }
      },
    },
  },
  methods: {
    ...mapActions('users', ['loadCurrentUser', 'loadAchievements']),
    ...mapActions('programs', ['loadPrograms']),
    ...mapActions('org', ['loadOrgSettings', 'loadUsageLimits']),
    ...mapActions('teams', ['resolveActiveTeam']),
    redirectToLogin() {
      if (this.isUserLoggedIn) return;

      const redirect = this.$route.fullPath;
      const query = redirect === '/' ? {} : { redirect };
      this.$router.push({ name: 'app-login', query });
    },

    checkAchievements() {
      if (!this.currentUser?.owner) return Promise.resolve();
      return this.loadAchievements();
    },

    checkHomePage() {
      if (!this.isVectorRecruiting) return;

      if (this.$route.name === 'home') {
        this.$router.push({ name: 'recruiting' });
      }
    },

    async initializeAuthenticatedApp() {
      this.checkHomePage();

      await Promise.allSettled([
        this.loadUsageLimits(),
        this.checkAchievements(),
        this.loadPrograms(),
        this.loadOrgSettings(),
      ]);
    },

    async checkCurrentUser() {
      try {
        await this.loadCurrentUser();

        if (!this.isUserLoggedIn) {
          this.redirectToLogin();
          return;
        }

        // Team-scoped requests must not fire before the tab's team is known.
        await this.resolveActiveTeam().catch(() => null);
        this.teamResolved = true;

        await this.initializeAuthenticatedApp();
      } catch (error) {
        this.redirectToLogin();
      } finally {
        this.teamResolved = true;
      }
    },

    shouldRefreshIntercomJwt() {
      return (
        this.isUserLoggedIn &&
        !inIframe &&
        document.visibilityState === 'visible' &&
        !this.jwtRefreshing
      );
    },

    async refreshIntercomJwtOnFocus() {
      if (!this.shouldRefreshIntercomJwt()) return;

      this.jwtRefreshing = true;
      try {
        const userInfo = await api().get('users/current');
        const intercomJwt = userInfo?.intercom_user_jwt;
        if (intercomJwt) {
          updateIntercom({ intercom_user_jwt: intercomJwt });
        }
      } finally {
        this.jwtRefreshing = false;
      }
    },

    // A count keeps layout state correct when docked panels overlap.
    handleDockedSidebarChange(active) {
      this.dockedSidebarCount = Math.max(
        0,
        this.dockedSidebarCount + (active ? 1 : -1)
      );
      this.applySidebarLayout(this.dockedSidebarCount > 0);
    },

    applySidebarLayout(active) {
      const wrap = this.$el.querySelector('.v-application--wrap');
      if (!wrap) return;
      wrap.classList.toggle('cy-sidebar-active', active);

      this.$nextTick(() => {
        const header = this.$el.querySelector('.app-header');
        if (!header) return;

        if (active) {
          header.style.setProperty('position', 'absolute', 'important');
          header.style.setProperty('left', '0px', 'important');
          header.style.setProperty('right', '0px', 'important');
          header.style.setProperty('width', 'auto', 'important');
          return;
        }

        header.style.removeProperty('position');
        header.style.removeProperty('left');
        header.style.removeProperty('right');
        header.style.removeProperty('width');
      });
    },
  },
  created() {
    initAppAnalytics();
    this.checkCurrentUser();
    EventBus.$on('dockedPanelSidebarChange', this.handleDockedSidebarChange);
  },
  mounted() {
    intercom.init();
    document.addEventListener(
      'visibilitychange',
      this.refreshIntercomJwtOnFocus
    );
  },
  beforeDestroy() {
    clearTeamInTab();
    this.applySidebarLayout(false);
    EventBus.$off('dockedPanelSidebarChange', this.handleDockedSidebarChange);
    document.removeEventListener(
      'visibilitychange',
      this.refreshIntercomJwtOnFocus
    );
  },
};
</script>
